import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";

import { useMobileKeyboardFocus } from "hooks/useMobileKeyboardFocus";

import { auth, checkEmail } from "store/features/authSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ModalType, openModal } from "store/features/modalSlice";

import { AuthFormValues, getAuthValidationSchema } from "./validation";

import featureAnnouncementService from "services/featureAnnouncements";
import { ONBOARDING_MODAL_CONTENT } from "shared/constants/modalContent";
import { routes } from "routes/routes";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { FbPixelAnalyticsEvents } from "shared/constants/fb-pixel-analytics";

export enum AuthStep {
  CheckEmail = "checkEmail",
  Auth = "auth",
}

export const useAuthForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<AuthStep>(AuthStep.CheckEmail);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);

  const { targetRef, triggerFocus, ProxyInput } = useMobileKeyboardFocus(
    step === AuthStep.Auth,
  );

  const authState = useAppSelector((state) => state.auth);
  const { isLoading, isSocialMediaLoading } = authState;

  const authValidationSchema = getAuthValidationSchema(step === AuthStep.Auth);

  const methods = useForm<AuthFormValues>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: yupResolver(authValidationSchema),
  });

  useEffect(() => {
    const subscription = methods.watch((_value, { name }) => {
      if (name && methods.formState.errors[name as keyof AuthFormValues]) {
        methods.trigger(name as keyof AuthFormValues);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const onCheckEmail = async (values: AuthFormValues) => {
    analytics().sendConversionEvent(
      AnalyticsEvents.auth_check_email,
      null,
      true,
    );

    const isNewUser = await dispatch(
      checkEmail({ email: values.email }),
    ).unwrap();

    setIsNewUser(isNewUser);
    setStep(AuthStep.Auth);
  };

  const onAuthenticate = async (values: AuthFormValues) => {
    try {
      analytics().sendConversionEvent(
        AnalyticsEvents.auth_with_email,
        null,
        true,
      );

      const data = await dispatch(
        auth({
          email: values.email,
          password: values.password,
        }),
      ).unwrap();

      if (data?.isNewUser) {
        analytics().sendConversionEvent(
          FbPixelAnalyticsEvents.CompleteRegistration,
        );
        analytics().sendConversionEvent(FbPixelAnalyticsEvents.StartTrial);

        dispatch(openModal(ModalType.VIDEO, ONBOARDING_MODAL_CONTENT));
        ONBOARDING_MODAL_CONTENT.featureId &&
          featureAnnouncementService().recordInitialView(
            ONBOARDING_MODAL_CONTENT.featureId,
          );
      }

      const from = location.state?.from || routes.home;
      navigate(from, { replace: true });
    } catch {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };

  const onForgotPassword = () => {
    const email = methods.getValues("email");

    analytics().sendEvent(AnalyticsEvents.auth_forgot_password);
    navigate(routes.forgotPassword, { state: { email: email } });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    methods.handleSubmit(async (values) => {
      if (step === AuthStep.CheckEmail) {
        triggerFocus();
        await onCheckEmail(values);
      } else {
        await onAuthenticate(values);
      }
    })();
  };

  return {
    step,
    isNewUser,
    isLoading,
    isSocialMediaLoading,
    methods,
    targetRef,
    ProxyInput,
    handleFormSubmit,
    onForgotPassword,
  };
};
